<script lang="ts" module>
  import { preventNavIf } from '#lib/utils/svelte.js';
  import Fieldset from '#lib/components/Fieldset.svelte';
  import { refreshAll } from '$app/navigation';
  import { page } from '$app/state';
  import DataTable from '#lib/components/data-table/DataTable.svelte';
  import { asWorkData } from '#lib/utils/workData.svelte.js';
  import type { TableRow, TableRowLeaf } from '#lib/zod/output/TabularData.js';
  import type { PageData, Snapshot } from './$types';
  import type { FePortMultiEdit } from './HpEditSubModal.svelte';
  import HpAddModal from './HpAddModal.svelte';
  import HpEditSubModal from './HpEditSubModal.svelte';
  import DkcTable from './DkcTable.svelte';
  import PoolTable from './PoolTable.svelte';

  const portTableCols = <const>[
    { title: 'Speed', key: 'speed', aggregationStrategy: 'sum' },
    { type: 'parameter', title: 'Cables', parameter: 'cables' },
    { type: 'parameter', title: 'Switch', parameter: 'switch' },
    { type: 'parameter', title: 'Slot/Port', parameter: 'slot' },
    { type: 'parameter', title: 'Description', parameter: 'note' },
    { type: 'parameter', title: 'Covers the room', parameter: 'covers' },
    { type: 'parameter', title: 'SAN Env', parameter: 'san_env' },
    { title: 'Throughput', key: 'throughput', aggregationStrategy: 'sum' },
    { type: 'parameter', title: 'WWN', parameter: 'wwn' },
    { type: 'hasTag', title: 'Automation', tag: 'automation' },
  ];

  const portTableColsWithActive = <const>[
    ...portTableCols,
    { type: 'hasTag', title: 'Active', tag: 'active' },
  ];

  export type System = PageData['system'];
  export type VisibleDkcs = Exclude<System['hpDetails'], null>['dkcs'];
</script>

<script lang="ts">
  let { data } = $props();

  const workData = asWorkData(() => data.system);
  const { data: system, dirty } = $derived(workData);
  preventNavIf(() => dirty);

  let hpTab = $state<'dkcs' | 'ports' | 'pools'>('dkcs');

  async function saveSystem(e: Event) {
    e.preventDefault();

    const result = await fetch(page.url.pathname, {
      method: 'POST',
      body: JSON.stringify(system),
    });

    if (result.ok) {
      await refreshAll();
    }
  }

  function onPortClick(row: TableRow) {
    hpEditSubModal?.openModal(
      {
        type: 'fePort',
        item: {
          ...(row.parameters.original as any),
          ids: [(row.parameters.original as { id: number }).id],
        },
      } as FePortMultiEdit,
      row.parameters.parentActive as boolean,
    );
  }

  let activeOnly = $state(true);
  let visibleDkcs = $derived(system.hpDetails?.dkcs.filter(({ active }) => active || !activeOnly));

  let portTable = $derived.by(() => {
    if (!visibleDkcs) return [];

    const next = visibleDkcs?.flatMap((dkc) =>
      dkc.controllers
        .filter(({ active }) => active || !activeOnly)
        .flatMap((ctl) =>
          ctl.channelBoards
            .filter(({ active }) => active || !activeOnly)
            .flatMap((chb) => ({
              name: chb.name,
              tags: [],
              type: <const>'group',
              parameters: {
                dkc: dkc.name,
                ctl: ctl.name,
                chb: chb.name,
              },
              children: chb.ports
                .filter(({ active }) => active || !activeOnly)
                .map((port) => {
                  const tags = [];
                  const data: TableRowLeaf['data'] = {};

                  if (port.active) tags.push('active');
                  if (port.automation) tags.push('automation');

                  if (port.speed !== null)
                    data.speed = {
                      units: 'GB / s',
                      value: port.speed,
                    };

                  if (port.throughput !== null)
                    data.throughput = {
                      units: 'MB / s',
                      value: port.throughput,
                    };

                  return {
                    name: port.name,
                    parameters: {
                      ...port,
                      parentActive: chb.active,
                      original: port,
                    },
                    type: <const>'leaf',
                    tags,
                    data,
                  };
                }),
            })),
        ),
    );

    if (data.boardPairs) {
      const order = data.boardPairs.flatMap((o) => o);

      next.sort((a, b) => {
        const [aIdx, bIdx] = [a, b].map((o) => order.indexOf(o.name));

        // TODO: sort
        if (aIdx >= 0 && bIdx < 0) return -1;
        else if (aIdx < 0 && bIdx >= 0) return 1;
        else if (aIdx >= 0 && bIdx >= 0) return aIdx - bIdx;

        return a.name.localeCompare(b.name);
      });
    }

    return next;
  });

  export const snapshot: Snapshot = {
    capture: () => ({ hpTab }),
    restore: (prev) => {
      hpTab = prev.hpTab;
    },
  };

  let selectedRows: TableRow[] = $state([]);
  let hpAddModal = $state<HpAddModal>();
  let hpEditSubModal = $state<HpEditSubModal>();

  function editSelectedFePorts() {
    if (selectedRows.length === 1) {
      onPortClick({ detail: selectedRows[0] } as any);
      return;
    }

    hpEditSubModal?.openModal(
      {
        type: 'fePort',
        item: {
          name: 'Multiple ports',
          ids: selectedRows.map((r) => (r.parameters.original as { id: number }).id),
        },
      },
      selectedRows.every((r) => r.parameters.parentActive),
    );
  }
</script>

<form class="space-y-4" method="post" action="?/edit" onsubmit={saveSystem}>
  <input type="text" name="active" class="hidden" hidden value={system.active} />
  <section class="flex items-center">
    <h1>{system.name}</h1>
    <div class="mr-4 ml-auto flex flex-row flex-nowrap items-center gap-2">
      SortID:
      <input
        class="input input-sm"
        type="number"
        name="sortId"
        min="0"
        step="1"
        max="9999"
        bind:value={system.sortId}
      />
    </div>
    <label>
      <input
        type="checkbox"
        bind:checked={system.active}
        name="show-active"
        class="toggle toggle-primary toggle-lg"
      />
      Active
    </label>
  </section>
  <fieldset class="grid grid-cols-1 gap-x-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
    <Fieldset title="Name">
      <input class="input" type="text" name="name" required bind:value={system.name} tabindex="0" />
    </Fieldset>
    <Fieldset title="Maintainer ID">
      <input class="input" type="text" name="maintainerId" bind:value={system.maintainerId} />
    </Fieldset>
    <Fieldset title="Datacenter">
      <input type="number" name="datacenterId" class="hidden" value={system.datacenterId} />
      <select class="input" bind:value={system.datacenterId}>
        <option value={-1}>Unassigned</option>
        {#each data.datacenters as { value, label }}
          <option {value}>{label}</option>
        {/each}
      </select>
    </Fieldset>
    {#if system.hpDetails}
      <Fieldset title="Array model">
        <input class="input" type="text" name="model" bind:value={system.hpDetails.model} />
      </Fieldset>
      <Fieldset title="Physical Serial Number">
        <input
          class="input"
          type="text"
          name="serialNumber"
          bind:value={system.hpDetails.serialNumber}
        />
      </Fieldset>
      <Fieldset title="Virtual DKCs">
        <input class="input" type="text" name="dkc" bind:value={system.hpDetails.dkc} />
      </Fieldset>
      <Fieldset title="Management IP">
        <input
          class="input"
          type="text"
          name="managementIp"
          bind:value={system.hpDetails.managementIp}
        />
      </Fieldset>
      <Fieldset title="Room">
        <input class="input" type="text" name="room" bind:value={system.hpDetails.room} />
      </Fieldset>
      <Fieldset title="Rack">
        <input class="input" type="text" name="rack" bind:value={system.hpDetails.rack} />
      </Fieldset>
      <Fieldset title="Enable map editing">
        <fieldset class="flex justify-start">
          <input
            type="checkbox"
            bind:checked={system.hpDetails.editableMaps}
            name="editableMaps"
            class="toggle my-2"
          />
        </fieldset>
      </Fieldset>
    {/if}

    <div class="-col-end-1 mt-4 flex flex-col items-end gap-2">
      <div class="flex gap-2">
        <button
          class="btn btn-secondary"
          type="reset"
          onclick={(e) => {
            e.preventDefault();
            workData.reset();
          }}
          disabled={!dirty}>Reset</button
        >
        <button type="submit" class="btn btn-primary" disabled={!dirty}> Submit </button>
      </div>
      <p class="bg-secondary text-secondary-content max-w-80 rounded-xl p-2 text-left">
        For editing information related to FE ports click here:
        <a
          class="font-bold"
          href="https://dpdhl.sharepoint.com/:x:/r/teams/HPEBiW/Shared%20Documents/General/DHL_XP_port_connectivity.xlsm?d=w4b10869c17294771af293d0dd0324f0d&csf=1&web=1&e=iG6PV7"
        >
          DHL_XP_port_connectivity.xlsm
        </a>
      </p>
    </div>
  </fieldset>
</form>

{#if system.hpDetails && visibleDkcs}
  <div class="tabs tabs-border tabs-md border-base-300 mb-2 w-full border-b">
    {#snippet tab(value: string, label: string)}
      <input type="radio" bind:group={hpTab} name="hp-tab" {value} class="tab" aria-label={label} />
    {/snippet}
    {@render tab('dkcs', 'DKCs')}
    {@render tab('ports', 'FE Ports')}
    {@render tab('pools', 'Pools')}
    <div class="ml-auto flex items-center gap-4 pr-4 whitespace-nowrap">
      {#if data.system.hpDetails?.editableMaps && hpTab === 'ports'}
        <button
          class="btn btn-sm btn-accent"
          disabled={selectedRows.length === 0}
          onclick={editSelectedFePorts}
        >
          Edit selected
        </button>
      {/if}
      <label>
        <input
          type="checkbox"
          bind:checked={activeOnly}
          name="show-active-only"
          class="toggle toggle-primary"
        />
        Active only
      </label>
    </div>
  </div>

  {#if hpTab === 'dkcs'}
    {@const editable = !data.status}
    <DkcTable
      disabled={dirty}
      {editable}
      {activeOnly}
      hpEditSubModal={hpEditSubModal!}
      hpAddModal={hpAddModal!}
      {visibleDkcs}
      {system}
    />
  {:else if hpTab === 'ports'}
    {@const editable = data.system.hpDetails?.editableMaps}
    <DataTable
      rows={portTable ?? []}
      nameColTitle="<span class='text-accent'>CHB</span> > <span class='text-primary'>Port</span>"
      {nameFunction}
      cols={activeOnly ? portTableCols : portTableColsWithActive}
      showGroupAggregate={false}
      selectableRows={editable}
      onRowClick={editable ? onPortClick : undefined}
      onSelectedChange={(r) => (selectedRows = r)}
    />
  {:else if hpTab === 'pools'}
    <PoolTable {activeOnly} {system} />
  {/if}
{/if}

{#snippet nameFunction(row: TableRow)}
  {#if row.type === 'group'}
    <span>{row.parameters.dkc}</span> >
    <span class="text-info">{row.parameters.ctl}</span> >
    <span class="text-accent">{row.name}</span>
  {:else}
    <span class="text-primary">{row.name}</span>
  {/if}
{/snippet}

<HpAddModal bind:this={hpAddModal} />
<HpEditSubModal bind:this={hpEditSubModal} />
